import { notification } from 'antd';

import i18next from '@/i18n';
import { setClientToken } from '@/services/user-hash';

import { BANKS_LEASING_MODELS } from '../AutoCredit/shared/constants/leasing';
import type {
  ICreateAutoCredit,
  ICreateAutoCreditAdditionalOptions
} from '../AutoCredit/types/api.types';
import { isInWebView } from '../shared/webview/env';
import type { ILoginClientCredentials } from '../types/client.types';
import type {
  AdditionalPackageInterface,
  BrandInterface,
  CityInterface,
  EquipmentInterface,
  IGetDictionariesList,
  ModelInterface
} from '../types/dictionaries.types';
import type {
  DealerItemResponseInterface,
  DealersListItemResponseInterface
} from '../types/store.types';
import { ClientApi } from './client.api';

interface IValidateClientOtpResponse {
  result: boolean;
  message: string;
  accessToken: string;
  existingActiveRequest: {
    onlineRequest: {
      uuid: string;
    };
  };
}

@InjectLeasingCompany
export class ClientCreateApi extends ClientApi {
  private leasingCompany: string | null = null;

  constructor(leasingCompany: string | null) {
    super();
    this.leasingCompany = leasingCompany;
  }

  setLeasingCompany(leasingCompany: string) {
    if (!isInWebView()) return;
    // Необходимо для настройки лизинговой компании в webview
    this.leasingCompany = leasingCompany;
  }

  async authValidateOtp(body: ILoginClientCredentials & { otp: string }) {
    const response = await this.call<
      ILoginClientCredentials,
      { accessToken: string }
    >({
      path: 'online/auth/validate-otp',
      method: 'post',
      body
    });

    if (response.accessToken) {
      setClientToken({ accessToken: response.accessToken, iin: body.iin });
    }

    return response;
  }

  resendValidationOtp(body: {
    iin: string;
    phoneNumber: string;
  }): Promise<unknown> {
    return this.call({
      path: 'online/auth/resend-otp',
      method: 'post',
      body
    });
  }

  createAutoCreditOrder(
    body: ICreateAutoCredit,
    additionalOptions?: ICreateAutoCreditAdditionalOptions
  ): Promise<{ uuid: string }> {
    const dto: ICreateAutoCredit = { ...body };

    if (this.leasingCompany) dto.sendTo = 'leasing';
    else if (
      additionalOptions &&
      BANKS_LEASING_MODELS.some(
        (model) =>
          model.cityName === additionalOptions.cityName &&
          model.brands.some(
            (brand) =>
              brand.brandCode === additionalOptions.brandCode &&
              brand.modelNames.includes(additionalOptions.modelName)
          )
      )
    ) {
      dto.sendTo = 'banks_leasing';
    } else dto.sendTo = 'banks';

    return this.call({
      path: 'online/requests',
      method: 'post',
      body: dto
    });
  }

  validateClientOtp(data: {
    requestId: string;
    otp: string;
  }): Promise<IValidateClientOtpResponse> {
    return this.call({
      path: `online/requests/${data.requestId}/validate-otp`,
      method: 'post',
      body: { otp: data.otp }
    });
  }

  resendClientOtp(requestId: string) {
    return this.call({
      path: `online/requests/${requestId}/resend-otp`,
      method: 'post'
    });
  }

  async getOnlineDictionaries(): Promise<IGetDictionariesList> {
    const response = await this.call<unknown, IGetDictionariesList>({
      path: 'online/dictionaries'
    });
    if (response.leasingCompanies?.length && this.leasingCompany) {
      if (
        !response.leasingCompanies.some(
          (company) => company.code === this.leasingCompany
        )
      ) {
        // Если лизинговая компания не найдена, то ведем стандартный процесс создания заявки
        this.leasingCompany = null;
        notification.warning({
          title: i18next.t('client.unknown-leasing-company'),
          description: i18next.t('client.unknown-leasing-company-description')
        });
      }
    }
    return response;
  }

  getAvailableCities(brandId?: string): Promise<CityInterface[]> {
    return this.call({
      path: 'online/cities',
      params: {
        brandId,
        leasingCompanyCode: this.leasingCompany ?? undefined
      }
    });
  }

  getBrandsByCity(params: {
    cityId: string;
    dealerId?: string;
  }): Promise<BrandInterface[]> {
    return this.call({
      path: `online/cities/${params.cityId}/brands`,
      params: {
        dealerId: params.dealerId,
        leasingCompanyCode: this.leasingCompany ?? undefined
      }
    });
  }

  getModelsByBrandAndCity(params: {
    brandId: string;
    cityId: string;
  }): Promise<ModelInterface[]> {
    return this.call({
      path: `online/brands/${params.brandId}/cities/${params.cityId}/models`,
      params: { leasingCompanyCode: this.leasingCompany ?? undefined }
    });
  }

  getEquipmentsByModel(modelId: string): Promise<EquipmentInterface[]> {
    return this.call({
      path: `online/models/${modelId}/equipments`,
      params: { leasingCompanyCode: this.leasingCompany ?? undefined }
    });
  }

  getExtraProducts(): Promise<AdditionalPackageInterface[]> {
    return this.call({ path: 'online/extra-products' });
  }

  getDealersByCityAndBrand(params: {
    cityId: string;
    brandId: string;
  }): Promise<DealersListItemResponseInterface[]> {
    return this.call({
      path: 'online/dealers',
      params: {
        ...params,
        leasingCompanyCode: this.leasingCompany ?? undefined
      }
    });
  }

  getDealerById(dealerId: string): Promise<DealerItemResponseInterface> {
    return this.call({ path: `online/dealers/${dealerId}` });
  }
}

export const getLeasingCompanyFromParams = () => {
  if (typeof window === 'undefined') return null;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('leasing') || null;
};

export const clientCreateApi = new ClientCreateApi(
  getLeasingCompanyFromParams()
);

// Декоратор для внедрения лизинговой компании в класс
function InjectLeasingCompany<T extends new (...args: any[]) => any>(
  value: T
): T {
  return class extends value {
    constructor(...args: any[]) {
      super(...args);

      // Перебираем все свойства класса
      for (const key of Object.getOwnPropertyNames(value.prototype)) {
        if (key === 'constructor') continue;

        const descriptor = Object.getOwnPropertyDescriptor(
          value.prototype,
          key
        );

        // Нам нужны только методы (функции), кроме самого конструктора
        if (descriptor && typeof descriptor.value === 'function') {
          const originalMethod = descriptor.value;

          // Перезаписываем метод, внедряя логику с лизинговой компанией
          this[key] = function (...methodArgs: any[]) {
            if (!this.leasingCompany) {
              const leasingCompany = getLeasingCompanyFromParams();
              if (leasingCompany) {
                this.leasingCompany = leasingCompany;
              }
            }
            return originalMethod.apply(this, methodArgs);
          };
        }
      }
    }
  } as unknown as T;
}
